import { useCallback } from "react";
import FilterPanel from "./FilterPanel";
import { useFilterHighlightMatrix } from "@/shared/hooks/useFilterHighlightMatrix";
import type { FilterMenuConfig } from "@/shared/models/filterTypes";

export interface FilterHighlightPanelsProps {
  filterMenus: FilterMenuConfig[];
  filterSelections: Record<string, string[]>;
  setFilterSelections: (next: Record<string, string[]>) => void;
  isFilterOpen: boolean;
  onClearFilter?: () => void;
  onCloseFilter?: () => void;

  highlightMenus: FilterMenuConfig[];
  highlightSelections: Record<string, string[]>;
  setHighlightSelections: (next: Record<string, string[]>) => void;
  isHighlightOpen: boolean;
  onClearHighlight?: () => void;
  onCloseHighlight?: () => void;

  persistKey?: string;
  /**
   * Opt in to tag-library search, on the Filter panel only.
   *
   * The two panels share one selections map keyed by section id, and a library
   * tag can only be written to a *filter* section — offering it on the
   * Highlight panel would silently filter the feed instead of highlighting it.
   */
  tagLibrary?: { menuId: string };

  /**
   * The widget's highlighted lists in order, and a way to move one: the first
   * a row matches paints it. From the widget's highlight hook
   * (useHighlightPanelMenu); shown in the Highlight panel.
   */
  highlightOrder?: string[];
  onMoveHighlight?: (key: string, delta: -1 | 1) => void;
}

/**
 * The Filter and Highlight panels for a feed widget.
 *
 * Both render the same unified list of watchlists, but each applies one verb:
 * the Filter panel offers filtering, the Highlight panel highlighting. They
 * are functionally different and each has its own header indicator, so showing
 * both columns in both panels buried at-a-glance state in a submenu.
 *
 * Owning this here keeps all 19 feed widgets identical by construction rather
 * than by discipline.
 */
export default function FilterHighlightPanels({
  filterMenus,
  filterSelections,
  setFilterSelections,
  isFilterOpen,
  onClearFilter,
  onCloseFilter,
  highlightMenus,
  highlightSelections,
  setHighlightSelections,
  isHighlightOpen,
  onClearHighlight,
  onCloseHighlight,
  persistKey,
  tagLibrary,
  highlightOrder,
  onMoveHighlight,
}: FilterHighlightPanelsProps) {
  const {
    groups,
    passthrough,
    selections,
    setSelections,
    highlightTotal,
    filterTotal,
  } =
    useFilterHighlightMatrix({
      highlightMenus,
      highlightSelections,
      setHighlightSelections,
      filterMenus,
      filterSelections,
      setFilterSelections,
    });

  const onToggle = useCallback(
    (sectionId: string, optionId: string, isDisabled: boolean) => {
      if (isDisabled) return;
      const current = selections[sectionId] || [];
      const next = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      setSelections({ ...selections, [sectionId]: next });
    },
    [selections, setSelections]
  );

  return (
    <>
      <FilterPanel
        matrixGroups={groups}
        onMatrixToggle={onToggle}
        filterMenus={passthrough}
        selections={selections}
        onChange={setSelections}
        isOpen={isFilterOpen}
        onClearSelections={onClearFilter}
        onClose={onCloseFilter}
        activeCount={filterTotal}
        persistKey={persistKey}
        tagLibrary={tagLibrary}
      />
      <FilterPanel
        matrixGroups={groups}
        onMatrixToggle={onToggle}
        filterMenus={passthrough}
        selections={selections}
        onChange={setSelections}
        isOpen={isHighlightOpen}
        onClearSelections={onClearHighlight}
        onClose={onCloseHighlight}
        activeCount={highlightTotal}
        persistKey={persistKey}
        variant="highlight"
        highlightOrder={highlightOrder}
        onMoveHighlight={onMoveHighlight}
      />
    </>
  );
}
